'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Carousel, CarouselApi, useCarousel } from '@/components/ui/carousel'
import { AssetCard } from '@/components/assets-explorer/asset-card'
import { useQuery } from '@tanstack/react-query'
import client from '@/lib/api/client'
import { Content } from '@/lib/categories/categories'
import useScrollToCarouselSelectedItem from '@/lib/hooks/useScrollToCarouselSelectedItem'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { cn } from '@/lib/utils'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa6'
import { useHover } from 'ahooks'
import useCountGridColumns from '@/lib/hooks/useCountGridColumns'
import { useParams } from 'next/navigation'
import assetCardStyles from '@/components/assets-explorer/asset-card.module.scss'
import { Skeleton } from '@/components/ui/skeleton'

const ASPECT_RATIO_DIVISOR = 2.3333 // 21:9

const ContentsSliderCarouselPrevious = ({ className }: { className?: string }) => {
  const { scrollPrev, canScrollPrev } = useCarousel()

  return (
    <button
      type="button"
      title="Scroll left"
      className={cn(
        'tw-absolute tw-left-0 tw-top-1/2 tw-flex tw-h-full tw-w-14 tw--translate-y-1/2 tw-items-center tw-justify-center tw-bg-opacity-70 tw-transition-all tw-duration-200 hover:tw-bg-[rgba(var(--Primary-rgb),var(--tw-bg-opacity,1))]',
        className
      )}
      onClick={scrollPrev}
      disabled={!canScrollPrev}
    >
      <FaChevronLeft className="tw-h-6 tw-w-6" />
      <span className="tw-sr-only">Previous slide</span>
    </button>
  )
}

const ContentsSliderCarouselNext = ({ className }: { className?: string }) => {
  const { scrollNext, canScrollNext } = useCarousel()

  return (
    <button
      type="button"
      title="Scroll right"
      className={cn(
        'tw-absolute tw-right-0 tw-top-1/2 tw-flex tw-h-full tw-w-14 tw--translate-y-1/2 tw-items-center tw-justify-center tw-bg-opacity-70 tw-transition-all tw-duration-200 hover:tw-bg-[rgba(var(--Primary-rgb),var(--tw-bg-opacity,1))]',
        className
      )}
      onClick={scrollNext}
      disabled={!canScrollNext}
    >
      <FaChevronRight className="tw-h-6 tw-w-6" />
      <span className="tw-sr-only">Next slide</span>
    </button>
  )
}

type ContentsSliderCarouselContentProps = {
  contents: Content[]
  asGrid: boolean
  itemSize: number
  contentSlug?: string
  onItemSelect?: (contentSlug: string) => void
}

const ContentsSliderCarouselContent = ({
  asGrid,
  contents,
  contentSlug,
  itemSize,
  onItemSelect,
}: ContentsSliderCarouselContentProps) => {
  const { carouselRef } = useCarousel()

  return (
    <div ref={carouselRef}>
      <ul
        className={cn('carousel-content tw-group', {
          '!tw--ml-2 tw-flex': !asGrid,
          '!tw--ml-0 tw-grid tw-grid-cols-[repeat(auto-fill,minmax(360px,1fr))] tw-gap-2': asGrid,
        })}
      >
        {React.Children.toArray(
          contents?.map((content, index) => (
            <li
              key={`contents-slider-${content.slug}-${index}`}
              className={cn(
                'carousel-item tw-z-[0] tw-min-w-0 tw-shrink-0 tw-grow-0 tw-basis-full tw-transition-opacity tw-duration-300 hover:tw-z-[1] hover:!tw-opacity-100',
                {
                  'group-hover:tw-opacity-65': content.slug !== contentSlug,
                  '!tw-pl-0': asGrid,
                  'tw-max-w-min !tw-pl-2': !asGrid,
                }
              )}
            >
              <div
                style={{
                  position: 'relative',
                  width: !asGrid ? `100%` : undefined,
                  minWidth: !asGrid ? `${itemSize}px` : undefined,
                }}
                className="tw-aspect-ultra-wide"
              >
                <AssetCard
                  className={cn(
                    content.slug !== contentSlug ? assetCardStyles.scaleOnHover : undefined,
                    !asGrid ? assetCardStyles.scaleOnHoverDisabled : undefined
                  )}
                  aspect="ultra-wide"
                  title={content.name}
                  image={content.image}
                  link={`/${content.mainCategorySlug}/${content.categorySlug}/${content.subCategorySlug}/${content.slug}`}
                  selected={content.slug === contentSlug}
                  onLinkClick={() => {
                    onItemSelect?.(content.slug)
                  }}
                />
              </div>
            </li>
          ))
        )}
      </ul>
    </div>
  )
}

export type ContentsSliderProps = {
  mainCategorySlug: string
  categorySlug: string
  subCategorySlug: string
  contentSlug?: string
  hideSingleContent?: boolean
  // When provided, we will use this data instead of fetching it. Useful for SSR
  initialContents?: Content[]
  // When true, we will try to get content with the slug provided in the URL
  fallbackContentSlug?: boolean
}

export default function ContentsSlider({
  mainCategorySlug,
  categorySlug,
  subCategorySlug,
  contentSlug: initialContentSlug,
  hideSingleContent,
  initialContents,
  fallbackContentSlug,
}: ContentsSliderProps) {
  const [api, setApi] = useState<CarouselApi>()

  const { content: pathContent } = useParams<{ content?: string }>()

  const resolvedContentSlug = useMemo(() => {
    return fallbackContentSlug ? initialContentSlug || pathContent : initialContentSlug
  }, [fallbackContentSlug, initialContentSlug, pathContent])

  const [contentSlug, setContentSlug] = useState<string | undefined>(resolvedContentSlug)
  const [scrollToContentSlug, setScrollToContentSlug] = useState<string | undefined>(resolvedContentSlug)
  const [startIndex, setStartIndex] = useState<number>(0)

  const carouselContentRef = useRef<HTMLDivElement>(null)
  const fakeGridRef = useRef<HTMLDivElement>(null)

  const { data: contents, isLoading: isLoadingContents } = useQuery({
    queryKey: ['contents', mainCategorySlug, categorySlug, subCategorySlug],
    queryFn: async ({ signal }) => {
      const { data } = await client.get<Content[]>(`/contents`, {
        params: {
          main_category: mainCategorySlug,
          category: categorySlug,
          sub_category: subCategorySlug,
        },
        signal,
      })
      return data
    },
    initialData: initialContents,
  })

  // When no content is found, show as grid
  const asGrid = useMemo(() => {
    return !contentSlug
  }, [contentSlug])

  const showContents = useMemo(() => {
    return !hideSingleContent || (contents && contents.length > 1)
  }, [contents, hideSingleContent])

  // Initial scroll
  const initiallySelectedContentIndex = useMemo(() => {
    return contents?.findIndex((content) => content.slug === scrollToContentSlug) ?? -1
  }, [scrollToContentSlug, contents])

  const selectedContentIndex = useMemo(() => {
    return contents?.findIndex((content) => content.slug === contentSlug) ?? -1
  }, [contentSlug, contents])

  const onScroll = useCallback(() => {
    setScrollToContentSlug(undefined)
  }, [])

  useScrollToCarouselSelectedItem({
    api,
    itemIndex: initiallySelectedContentIndex !== -1 ? initiallySelectedContentIndex : selectedContentIndex,
    forceScroll: true,
    onScroll,
  })

  const isHoveringCarouselContent = useHover(carouselContentRef)

  // Calculate the number of columns that can fit in the container
  const { itemSize, count: columns } = useCountGridColumns({
    target: fakeGridRef,
    minWidth: 360,
    gap: 8, // 0.5rem
  })

  // Calculate item height based on the width, take into account that the item is ultra-wide (21:9)
  const itemHeight = useMemo(() => {
    return itemSize / ASPECT_RATIO_DIVISOR
  }, [itemSize])

  // When asGrid changes, set the startIndex to 0
  useEffect(() => {
    if (asGrid) {
      setStartIndex(0)
    }
  }, [asGrid])

  // If resolvedContentSlug changes, set the content slug
  useEffect(() => {
    setContentSlug(resolvedContentSlug)
  }, [resolvedContentSlug])

  // Try to minimize layout shifting. Delay Carousel render until after the first render
  const [isMounted, setIsMounted] = useState(false)
  useEffect(() => {
    if (!isLoadingContents && !!contents && columns > 0) {
      setIsMounted(true)
    }
  }, [columns, contents, isLoadingContents])

  return (
    <div
      data-slot="contents-slider"
      className={cn('contents-slider', {
        'tw-overflow-hidden': !asGrid,
      })}
    >
      {/*Fake empty grid to take measurements for slider view, so it doesn't jump when switching views*/}
      <div
        ref={fakeGridRef}
        className="fake-grid tw-grid tw-grid-cols-[repeat(auto-fill,minmax(360px,1fr))] tw-gap-2 tw-overflow-x-auto"
      />

      {(!isMounted || isLoadingContents) && (
        <div className="tw-grid tw-grid-cols-[repeat(auto-fill,minmax(360px,1fr))] tw-gap-2 tw-overflow-x-auto">
          {Array.from({ length: columns }).map((_, index) => {
            return (
              <div
                key={index}
                className="tw-aspect-ultra-wide"
                style={{
                  width: '100%',
                  height: itemHeight,
                }}
              >
                <Skeleton className="tw-h-full tw-w-full" />
              </div>
            )
          })}
        </div>
      )}

      {isMounted && !isLoadingContents && showContents && (
        <Carousel
          setApi={setApi}
          opts={{
            active: !asGrid,
            align: 'start',
            loop: true,
            skipSnaps: true,
            startIndex,
          }}
          plugins={[
            WheelGesturesPlugin({
              forceWheelAxis: 'y',
            }),
          ]}
          className={cn('contents-slider-carousel', {
            '': !asGrid,
          })}
        >
          <div ref={carouselContentRef} className="tw-relative">
            <ContentsSliderCarouselContent
              contents={contents ?? []}
              contentSlug={contentSlug}
              asGrid={asGrid ?? false}
              itemSize={itemSize}
              onItemSelect={(slug) => {
                setContentSlug(slug)
              }}
            />

            {!asGrid && (
              <>
                {api?.canScrollPrev() && (
                  <ContentsSliderCarouselPrevious
                    className={cn({
                      'tw-opacity-0': !isHoveringCarouselContent,
                    })}
                  />
                )}
                {api?.canScrollNext() && (
                  <ContentsSliderCarouselNext
                    className={cn({
                      'tw-opacity-0': !isHoveringCarouselContent,
                    })}
                  />
                )}
              </>
            )}
          </div>
        </Carousel>
      )}
    </div>
  )
}
